import { Article } from './articles';

export interface MatchSnapshot {
  external_match_id?: string;
  competition?: string;
  date_time?: string;
  venue?: string;
  home?: string;
  away?: string;
  score?: string;
}

export function stripHtml(html?: string): string {
  if (!html) return '';
  if (typeof window === 'undefined') {
    return html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  }
  const div = document.createElement('div');
  div.innerHTML = html;
  return (div.textContent || div.innerText || '')?.replace(/\s+/g, ' ').trim();
}

export function composeInstagramPostFromArticle(params: {
  article: Article;
  trackingUrl: string;
  clubName?: string;
  hashtags?: string[];
  match?: MatchSnapshot | null;
}): string {
  const { article, trackingUrl, clubName, hashtags = [], match } = params;
  const title = article.title?.trim() || '';
  const catName = (article as any)?.category?.name || (article as any)?.category_name || '';
  const snippet = stripHtml(article.content).slice(0, 160);
  const defaultTags = hashtags.length ? hashtags : [
    `#${normalizeTag(clubName || 'FKKrnov')}`,
    '#fotbal',
    '#modrazluta',
  ];

  if (match && (match.home || match.away)) {
    const home = match.home || '';
    const away = match.away || '';
    const comp = match.competition ? `${match.competition}` : '';
    const date = match.date_time ? formatDateTime(match.date_time) : '';
    const score = match.score && /\d/.test(match.score) ? match.score : '';

    const header = `💙💛 ${(catName || clubName || 'Náš klub')}: ${title} 💛💙`;
    const lines = [
      header,
      '',
      score ? `Výsledek: ${home} ${score} ${away}` : `${home} vs ${away}`,
      comp || date ? `${comp}${comp && date ? ' • ' : ''}${date}` : '',
      match.venue ? `Místo: ${cleanVenue(String(match.venue))}` : '',
      '',
      snippet ? `${snippet}${snippet.length === 160 ? '…' : ''}` : '',
      '',
      '📸 Celý článek najdeš tady 👇',
      `🔗 ${trackingUrl}`,
      '',
      defaultTags.join(' '),
      '💙💛'
    ].filter(Boolean);
    return lines.join('\n');
  }

  // Informative/general article
  const header = `💙💛 ${(catName || clubName || 'Náš klub')}: ${title} 💛💙`;
  const lines = [
    header,
    '',
    snippet,
    '',
    '📸 Celý článek najdeš tady 👇',
    `🔗 ${trackingUrl}`,
    '',
    defaultTags.join(' '),
    '💙💛'
  ];
  return lines.join('\n');
}

export function composeInstagramPostFromActivity(params: {
  activity: any;
  trackingUrl: string;
  clubName?: string;
  hashtags?: string[];
}): string {
  const { activity, trackingUrl, clubName, hashtags = [] } = params;
  const title = String(activity?.title || '').trim();
  const desc = stripHtml(String(activity?.description || '')).slice(0, 280);
  const date = activity?.start_time ? formatDateTime(activity.start_time) : '';
  const place = activity?.location ? String(activity.location) : '';

  const defaultTags = hashtags.length ? hashtags : [
    `#${normalizeTag(clubName || 'FKKrnov')}`,
    '#aktivity',
    '#fotbal',
  ];

  const header = `💙💛 ${clubName || 'Náš klub'}: ${title} 💛💙`;
  const lines = [
    header,
    '',
    date || place ? `${date}${date && place ? ' • ' : ''}${place}` : '',
    desc,
    '',
    '📸 Více informací najdeš tady 👇',
    `🔗 ${trackingUrl}`,
    '',
    defaultTags.join(' '),
    '💙💛'
  ].filter(Boolean);
  return lines.join('\n');
}

export function formatDateTime(dt: string): string {
  const s = String(dt || '').trim();
  // Handle FAČR format: dd.mm.yyyy or dd.mm.yyyy HH:MM
  const m = s.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})(?:\s+(\d{1,2}):(\d{2}))?$/);
  if (m) {
    const dd = parseInt(m[1], 10);
    const MM = parseInt(m[2], 10);
    const yyyy = parseInt(m[3], 10);
    const hh = m[4] ? parseInt(m[4], 10) : 0;
    const min = m[5] ? parseInt(m[5], 10) : 0;
    const d = new Date(yyyy, MM - 1, dd, hh, min);
    const dateStr = d.toLocaleDateString('cs-CZ');
    const timeStr = (m[4] ? d.toLocaleTimeString('cs-CZ', { hour: '2-digit', minute: '2-digit' }) : '');
    return timeStr ? `${dateStr} ${timeStr}` : dateStr;
  }
  // ISO-like or other parseable formats
  try {
    const d = new Date(s);
    if (!isNaN(d.getTime())) {
      return `${d.toLocaleDateString('cs-CZ')} ${d.toLocaleTimeString('cs-CZ', { hour: '2-digit', minute: '2-digit' })}`;
    }
  } catch {}
  return s;
}

export function cleanVenue(v: string): string {
  try {
    const base = String(v || '').trim();
    // Prefer locality before first " - " (e.g., "Kobeřice - tráva" -> "Kobeřice")
    return base.split(' - ')[0].trim();
  } catch {
    return v;
  }
}

function normalizeTag(s: string): string {
  return s
    .replace(/[^\p{L}\p{N}]+/gu, '') // remove spaces/symbols
    .replace(/[áä]/gi, 'a')
    .replace(/[č]/gi, 'c')
    .replace(/[ď]/gi, 'd')
    .replace(/[éěë]/gi, 'e')
    .replace(/[íï]/gi, 'i')
    .replace(/[ľĺ]/gi, 'l')
    .replace(/[ň]/gi, 'n')
    .replace(/[óö]/gi, 'o')
    .replace(/[ř]/gi, 'r')
    .replace(/[š]/gi, 's')
    .replace(/[ť]/gi, 't')
    .replace(/[úůü]/gi, 'u')
    .replace(/[ý]/gi, 'y')
    .replace(/[ž]/gi, 'z');
}
